import { useEffect, useState } from 'react'
import { TrendingUp, Users, DollarSign, Target, RefreshCw } from 'lucide-react'
import { api } from '../services/api'
import type { DashboardData } from '../types'
import RevenueHeatmap from './RevenueHeatmap'
import TimeSeriesChart from './TimeSeriesChart'

export default function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  const fetchDashboard = async () => {
    try {
      setLoading(true)
      const dashboardData = await api.getDashboard()
      setData(dashboardData)
    } catch (error) {
      console.error('Failed to fetch dashboard:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    try {
      setRefreshing(true)
      await api.refreshMetrics()
      await fetchDashboard()
    } catch (error) {
      console.error('Failed to refresh:', error)
    } finally {
      setRefreshing(false)
    }
  }

  useEffect(() => {
    fetchDashboard()
    const interval = setInterval(fetchDashboard, 60000) // Auto-refresh every minute
    return () => clearInterval(interval)
  }, [])

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    )
  }

  const { overall, top_performers, recommendations } = data

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white">실시간 대시보드</h1>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-5 w-5 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          새로고침
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard
          title="총 매출"
          value={`$${overall.total_revenue.toLocaleString()}`}
          icon={<DollarSign className="h-6 w-6" />}
          trend="+12.5%"
          color="bg-green-500"
        />
        <KPICard
          title="총 리드"
          value={overall.total_leads.toLocaleString()}
          icon={<Users className="h-6 w-6" />}
          trend="+8.2%"
          color="bg-blue-500"
        />
        <KPICard
          title="총 전환"
          value={overall.total_conversions.toLocaleString()}
          icon={<Target className="h-6 w-6" />}
          trend="+15.3%"
          color="bg-purple-500"
        />
        <KPICard
          title="평균 전환율"
          value={`${(overall.avg_conversion_rate * 100).toFixed(2)}%`}
          icon={<TrendingUp className="h-6 w-6" />}
          trend="+2.1%"
          color="bg-sky-500"
        />
      </div>

      {/* Charts Row - 전체 너비 사용 */}
      <div className="space-y-6">
        <RevenueHeatmap />
        <TimeSeriesChart />
      </div>

      {/* Top Performers */}
      <div className="bg-slate-800 rounded-lg p-6">
        <h2 className="text-xl font-bold text-white mb-4">🏆 Top 3 성과자</h2>
        <div className="space-y-4">
          {top_performers.map((performer) => (
            <div
              key={performer.platform}
              className="flex items-center justify-between p-4 bg-slate-700 rounded-lg"
            >
              <div className="flex items-center space-x-4">
                <span className="text-2xl font-bold text-sky-400">
                  #{performer.rank}
                </span>
                <div>
                  <p className="font-semibold text-white">{performer.platform}</p>
                  <p className="text-sm text-gray-400">
                    전환율: {(performer.conversion_rate * 100).toFixed(2)}%
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-green-400">
                  ${performer.revenue.toLocaleString()}
                </p>
                <p className="text-sm text-gray-400">
                  ROI: {performer.roi.toFixed(0)}%
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recommendations */}
      <div className="bg-slate-800 rounded-lg p-6">
        <h2 className="text-xl font-bold text-white mb-4">💡 AI 권장사항</h2>
        <div className="space-y-2">
          {recommendations.map((rec, index) => (
            <div
              key={index}
              className="flex items-start space-x-3 p-3 bg-slate-700 rounded-lg"
            >
              <span className="text-2xl">{rec.split(' ')[0]}</span>
              <p className="text-gray-300">{rec.substring(3)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function KPICard({
  title,
  value,
  icon,
  trend,
  color,
}: {
  title: string
  value: string
  icon: React.ReactNode
  trend: string
  color: string
}) {
  return (
    <div className="bg-slate-800 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`${color} p-3 rounded-lg text-white`}>{icon}</div>
        <span className="text-sm text-green-400 font-semibold">{trend}</span>
      </div>
      <h3 className="text-gray-400 text-sm mb-1">{title}</h3>
      <p className="text-2xl font-bold text-white">{value}</p>
    </div>
  )
}
